import { useEffect, useState } from 'react';
import { Card, Drawer, DrawerSection, Segmented, StatusChip } from '../components/data';
import { ErrorBanner, Loading, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { downloadBlob, fileStamp } from '../lib/download';
import { num } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Me } from '../lib/types';
import { NocAgentHealth } from './noc/NocAgentHealth';
import { NocScorecard } from './noc/NocScorecard';
import { NocWorkload } from './noc/NocWorkload';
import type { NocAlertLibraryData, NocAlertRecord } from './noc/nocTypes';

/**
 * NOC — the alert knowledge base, moved in from `C:\Development\noc`.
 *
 * A searchable library of 478 deduplicated real NOC alerts, each carrying the
 * symptom, cause, impact, remediation and troubleshooting history an engineer
 * wrote at the time. This is what the NOC card opens.
 *
 * WHAT CAME ACROSS AND WHAT DID NOT
 *
 * That app had six dashboards. Five of them — alert correlation, backups,
 * preventative maintenance, estate health and automation value — were computed
 * from a seeded generator against fourteen fictional clients, because there is
 * no RMM or PSA feed into this database for them to read. They were ported and
 * then removed: a dashboard of invented numbers inside the toolbox the
 * leadership team actually runs on is worse than no dashboard, and labelling it
 * "sample data" only made it less dangerous, not useful.
 *
 * The sixth was real. It is this page.
 *
 * ESTATE HEALTH IS BACK, and on the only terms that were ever on offer: a feed.
 * ConnectWise Automate is connected under Settings › Integrations, and the tab
 * reads the agents live from it (noc/NocAgentHealth.tsx). Nothing is generated
 * and nothing is stored — which is also why it carries Automate's own client
 * names rather than joining to `core.Client`; that join is still the thing to
 * build, and still wants tables of its own.
 *
 * The other three remain in the standalone app. See the NOC section of the
 * README.
 *
 * Mounted behind the NOC page grant. See App.tsx and the API's requirePage('noc').
 *
 * Three tabs. The library: search, filters, the list and the record; an admin
 * can update it from Autotask — the alert names the last 7, 30 or 90 days of
 * alert tickets show that the library does not document are built into new
 * entries the library's own way (api lib/noc/autotaskAlerts.ts). Alert
 * workload: the alert tickets, live from Autotask for up to 90 days, counted
 * the way the original spreadsheet was (noc/NocWorkload.tsx). And Estate
 * health: every agent, live from Automate, and what is wrong with it
 * (noc/NocAgentHealth.tsx).
 */

/* The section headings the importer parsed out of each record, in the order an
   engineer reads them: what happened, why, what it broke, what we did. Any
   section the parser did not find is skipped rather than shown empty. */
const SECTION_LABELS: Record<string, string> = {
  description: 'Description',
  cause: 'Cause',
  impact: 'Impact',
  analysis: 'NOC analysis',
  action: 'Action to be taken',
  troubleshooting: 'Troubleshooting performed',
  status: 'Current status',
};

const SECTION_ORDER = [
  'description',
  'cause',
  'impact',
  'analysis',
  'action',
  'troubleshooting',
  'status',
];

/** The full record for one alert, opened from the list. */
function AlertDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, loading, error } = useResource<NocAlertRecord>(
    id ? `/noc/alert-library/alert?id=${encodeURIComponent(id)}` : null,
  );
  const open = Boolean(id);
  if (!open) return null;

  const ordered = data ? SECTION_ORDER.filter((key) => data.sections[key]) : [];
  const unparsed = data && ordered.length === 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={data?.title ?? 'Alert'}
      subtitle={
        data
          ? `${data.id} · ${data.category}${data.alertName ? ` · ${data.alertName}` : ''}`
          : 'Loading…'
      }
    >
      {error && <ErrorBanner message={error} />}
      {loading && !data && <Loading label="Opening the record" />}

      {data && (
        <>
          <div className="noc-pills mb-4">
            <StatusChip band={data.band}>{data.severity}</StatusChip>
            {data.site && <span className="noc-pill">Site · {data.site}</span>}
            {data.resource && <span className="noc-pill">Resource · {data.resource}</span>}
            {data.family && data.family !== data.category && (
              <span className="noc-pill">{data.family}</span>
            )}
            {data.seen && (
              <span className="noc-pill">
                Fired in {num(data.seen.tickets)} tickets · last {data.seen.lastSeen}
              </span>
            )}
          </div>

          {ordered.map((key) => (
            <DrawerSection key={key} title={SECTION_LABELS[key] ?? key}>
              <p className="noc-alert-text">{data.sections[key]}</p>
            </DrawerSection>
          ))}

          {/* The original text is kept even when the sections parsed cleanly:
              these are real engineer write-ups, and the parser's view of one is
              a convenience, not the record. */}
          <DrawerSection
            title={unparsed ? 'Alert detail' : 'Original alert text'}
            count={`${num(data.words)} words`}
          >
            <p className="noc-alert-text raw">{data.body}</p>
          </DrawerSection>

          {data.links.length > 0 && (
            <DrawerSection title="Referenced knowledge base">
              <ul className="noc-links">
                {data.links.map((href) => (
                  <li key={href}>
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {href}
                    </a>
                  </li>
                ))}
              </ul>
            </DrawerSection>
          )}
        </>
      )}
    </Drawer>
  );
}

export function NocPage() {
  const [tab, setTab] = useState<'library' | 'workload' | 'health' | 'scorecard'>('library');
  const [updateDays, setUpdateDays] = useState<7 | 30 | 90>(30);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [limit, setLimit] = useState(40);
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /* Debounced: the search runs server-side across the full text of every record,
     so a request per keystroke would be 478 full-text scans a second. */
  useEffect(() => {
    const t = setTimeout(() => setQ(term), 220);
    return () => clearTimeout(t);
  }, [term]);

  /* Any filter change resets paging — otherwise a narrowed search inherits
     "show 200" from the last one and the page jumps. */
  useEffect(() => setLimit(40), [q, category, severity]);

  const params = new URLSearchParams({ category, severity, limit: String(limit) });
  if (q) params.set('q', q);
  const { data, loading, error, reload } = useResource<NocAlertLibraryData>(
    `/noc/alert-library?${params.toString()}`,
  );
  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';
  const [updating, setUpdating] = useState(false);
  const [updateNote, setUpdateNote] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  /** Builds new entries from the last 7, 30 or 90 days of Autotask alert tickets. */
  async function updateFromAutotask() {
    setUpdating(true);
    setUpdateError(null);
    setUpdateNote(null);
    try {
      const r = await api.post<{
        ticketsRead: number;
        alertNames: number;
        alreadyDocumented: number;
        addedOrUpdated: number;
        libraryTotal: number;
        days: number;
      }>('/noc/alert-library/update-from-autotask', { days: updateDays });
      setUpdateNote(
        `Read ${num(r.ticketsRead)} alert tickets from the last ${r.days} days: ${num(r.alertNames)} alert names, ` +
          `${num(r.alreadyDocumented)} already in the library, ${num(r.addedOrUpdated)} added or updated. ` +
          `The library now holds ${num(r.libraryTotal)} alerts.`,
      );
      reload();
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Could not update from Autotask');
    } finally {
      setUpdating(false);
    }
  }

  /**
   * The library as a Word document, built server-side.
   *
   * Fetched rather than linked: the endpoint is behind the same bearer token as
   * everything else, and a plain `<a href>` in a new tab carries no
   * Authorization header — the same reason the SOP viewer and the job
   * description library go through the API client. The filters travel with it,
   * so what downloads is what is on screen.
   */
  async function exportDocx() {
    setExporting(true);
    setExportError(null);
    const scope = new URLSearchParams({ category, severity });
    if (q) scope.set('q', q);
    try {
      const blob = await api.getBlob(`/noc/alert-library/export.docx?${scope.toString()}`);
      downloadBlob(`noc-alert-library-${fileStamp()}.docx`, blob);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Could not build the document');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <PageHead
        title="NOC"
        lede="The alert library — real NOC alerts with the cause, impact and remediation recorded against each one — the workload those alerts create, from the Autotask alert tickets, and the health of the estate, live from Automate."
        actions={
          tab === 'library' && <div className="row" style={{ gap: 'var(--space-2)' }}>
            {isAdmin && (
              <select
                className="select"
                value={updateDays}
                onChange={(e) => setUpdateDays(Number(e.target.value) as 7 | 30 | 90)}
                disabled={updating}
                aria-label="How far back the update reads"
                title="How far back the update reads alert tickets"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            )}
            {isAdmin && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={updating}
                onClick={() => void updateFromAutotask()}
                title="Add the alert types Autotask's alert tickets show that the library does not document yet"
              >
                {updating ? 'Reading Autotask…' : 'Update from Autotask'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              disabled={exporting || !data || data.total === 0}
              onClick={() => void exportDocx()}
              title="Everything the filters below currently match, as a Word document"
            >
              {exporting ? 'Building the document…' : `Export ${data ? num(data.total) : ''} to Word`}
            </button>
          </div>
        }
      />

      <div className="tabs">
        <button type="button" className={`tab${tab === 'library' ? ' active' : ''}`} onClick={() => setTab('library')}>
          Alert library
        </button>
        <button type="button" className={`tab${tab === 'workload' ? ' active' : ''}`} onClick={() => setTab('workload')}>
          Alert workload
        </button>
        <button type="button" className={`tab${tab === 'health' ? ' active' : ''}`} onClick={() => setTab('health')}>
          Estate health
        </button>
        <button type="button" className={`tab${tab === 'scorecard' ? ' active' : ''}`} onClick={() => setTab('scorecard')}>
          Scorecard
        </button>
      </div>

      {tab === 'workload' && <NocWorkload onOpenAlert={setOpenId} />}
      {tab === 'health' && <NocAgentHealth />}
      {tab === 'scorecard' && <NocScorecard />}

      {tab === 'library' && updating && (
        <Loading label={`Reading the last ${updateDays} days of alert tickets from Autotask`} />
      )}
      {tab === 'library' && updateNote && <p className="sub mb-4">{updateNote}</p>}
      {tab === 'library' && updateError && <ErrorBanner message={updateError} />}
      {tab === 'library' && exportError && <ErrorBanner message={exportError} />}
      {tab === 'library' && error && <ErrorBanner message={error} />}
      {tab === 'library' && loading && !data && <Loading label="Loading the alert library" />}

      {tab === 'library' && data && (
        <div className="noc-page">
          {/* One filter row above everything it scopes. */}
          <Card>
            <div className="noc-library-filters">
              <div className="field grow">
                <label htmlFor="noc-alert-search">Search</label>
                <input
                  id="noc-alert-search"
                  type="search"
                  className="input"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Symptom, cause, impact, remediation, hostname…"
                />
              </div>

              <div className="field">
                <label htmlFor="noc-alert-category">Category</label>
                <select
                  id="noc-alert-category"
                  className="select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="all">All categories</option>
                  {data.meta.categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.count})
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Severity</label>
                <Segmented
                  label="Severity"
                  value={severity}
                  onChange={setSeverity}
                  options={[
                    { id: 'all', label: 'All' },
                    ...data.meta.severities.map((s) => ({ id: s.id, label: s.id })),
                  ]}
                />
              </div>

              {(Boolean(q) || category !== 'all' || severity !== 'all') && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setTerm('');
                    setCategory('all');
                    setSeverity('all');
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </Card>

          <LibraryBody data={data} onOpen={setOpenId} onMore={() => setLimit((n) => n + 40)} />
        </div>
      )}

      <AlertDrawer id={openId} onClose={() => setOpenId(null)} />
    </>
  );
}

/**
 * The list.
 *
 * Split out only so the filter row above stays readable — it has no state of its
 * own.
 */
function LibraryBody({
  data,
  onOpen,
  onMore,
}: {
  data: NocAlertLibraryData;
  onOpen: (id: string) => void;
  onMore: () => void;
}) {
  const { meta, results, total, hasMore } = data;
  /* "Filtered" is derived from the RESPONSE rather than the inputs, so the
     facet captions never describe a filter the shown results do not reflect. */
  const filtered = total !== meta.totals.alerts;

  return (
    <>
      <Card
        title="Alert library"
        subtitle={
          filtered
            ? `${num(total)} of ${num(meta.totals.alerts)} alerts match — select one to read the full record`
            : `All ${num(total)} alerts, most severe first — select one to read the full record`
        }
      >
        {/* Stated from the export's own flag, so it cannot quietly stop being
            true if an unredacted file is ever dropped in. */}
        <p className="xs muted mb-4">
          {num(meta.totals.alerts - meta.fromAutotask.count)} imported {new Date(meta.importedAt).toLocaleDateString()} from{' '}
          {meta.source}
          {meta.fromAutotask.count > 0 &&
            ` · ${num(meta.fromAutotask.count)} built from Autotask alert tickets` +
              (meta.fromAutotask.updatedUtc ? `, last updated ${new Date(meta.fromAutotask.updatedUtc).toLocaleDateString()}` : '')}
          {meta.redacted ? ' · client and host names redacted' : ''}.
        </p>
        {results.length === 0 ? (
          <p className="sub">No alerts match those filters. Try a broader search term.</p>
        ) : (
          <ul className="noc-alert-list">
            {results.map((alert) => (
              <li key={alert.id}>
                <button type="button" className="noc-alert-row" onClick={() => onOpen(alert.id)}>
                  <span className="head">
                    <StatusChip band={alert.band}>{alert.severity}</StatusChip>
                    <span className="title">{alert.title}</span>
                  </span>
                  <span className="meta">
                    {alert.id} · {alert.category}
                    {alert.site ? ` · ${alert.site}` : ''}
                    {alert.resource ? ` · ${alert.resource}` : ''}
                    {alert.seen ? ` · fired in ${num(alert.seen.tickets)} tickets` : ''}
                  </span>
                  <span className="snippet">{alert.snippet}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {hasMore && (
          <button type="button" className="btn btn-ghost mt-4" onClick={onMore}>
            Show 40 more — {num(total - results.length)} remaining
          </button>
        )}
      </Card>
    </>
  );
}
